import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Loader2, Mail, CheckCircle, ArrowLeft } from 'lucide-react';
import useAuthStore from '../store/authStore';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// ── Modal "Esqueci minha senha" ───────────────────────────────
function ModalEsqueciSenha({ onFechar }) {
  const [email,    setEmail]    = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviado,  setEnviado]  = useState(false);

  const enviar = async (e) => {
    e.preventDefault();
    if (!email) return toast.error('Informe seu e-mail.');
    setEnviando(true);
    try {
      const r = await fetch(`${API}/auth/esqueci-senha`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.erro);
      setEnviado(true);
    } catch (err) {
      toast.error(err.message || 'Erro ao enviar. Tente novamente.');
    }
    setEnviando(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        {!enviado ? (
          <>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-acre-green/10 rounded-xl flex items-center justify-center">
                <Mail size={18} className="text-acre-green"/>
              </div>
              <div>
                <p className="font-sora font-bold text-acre-dark">Esqueceu a senha?</p>
                <p className="text-xs text-gray-400">Enviaremos um link de recuperação</p>
              </div>
            </div>

            <form onSubmit={enviar} className="space-y-4">
              <div>
                <label className="label">E-mail cadastrado *</label>
                <input className="input" type="email" placeholder="seu@email.com"
                  value={email} onChange={e => setEmail(e.target.value)} required autoFocus/>
                <p className="text-xs text-gray-400 mt-1">
                  Enviaremos um link válido por 2 horas para redefinir sua senha.
                </p>
              </div>

              <button type="submit" disabled={enviando}
                className="btn-primary w-full justify-center py-3 flex items-center gap-2 disabled:opacity-60">
                {enviando ? <Loader2 size={14} className="animate-spin"/> : <Mail size={14}/>}
                Enviar link de recuperação
              </button>
            </form>

            <button onClick={onFechar} className="w-full mt-3 text-sm text-gray-400 hover:text-gray-600 flex items-center justify-center gap-1.5">
              <ArrowLeft size={13}/> Voltar ao login
            </button>
          </>
        ) : (
          <div className="text-center py-2">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={28} className="text-green-500"/>
            </div>
            <h3 className="font-sora font-bold text-acre-dark text-lg mb-2">E-mail enviado!</h3>
            <p className="text-sm text-gray-500 mb-1">
              Se <strong>{email}</strong> estiver cadastrado, você receberá um e-mail com o link de redefinição.
            </p>
            <p className="text-xs text-gray-400 mb-5">Verifique sua caixa de entrada e a pasta de spam.</p>
            <button onClick={onFechar} className="btn-primary w-full justify-center py-2.5 text-sm">
              Voltar ao login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Página de login ───────────────────────────────────────────
export default function Login() {
  const { login, loading } = useAuthStore();
  const navigate  = useNavigate();
  const [params]  = useSearchParams();
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [verSenha, setVerSenha]   = useState(false);
  const [modalEsq, setModalEsq]  = useState(false);

  const onSubmit = async (data) => {
    const res = await login(data.email, data.senha);
    if (res.success) {
      toast.success('Bem-vindo(a) de volta!');
      const dest = { admin: '/admin', empresa: '/empresa', candidato: '/candidato' };
      navigate(dest[res.tipo] || '/');
    } else {
      toast.error(res.erro);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-acre-dark to-[#1a4a2e] flex items-center justify-center p-4">

      {modalEsq && <ModalEsqueciSenha onFechar={() => setModalEsq(false)}/>}

      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <span className="text-3xl">🌿</span>
            <span className="font-sora font-bold text-3xl text-white">
              Acre<span className="text-acre-gold">Job</span>
            </span>
          </Link>
          <p className="text-white/60 text-sm mt-2">Conectando talentos e oportunidades</p>
        </div>

        {params.get('sessao') === 'expirada' && (
          <div className="bg-yellow-500/20 border border-yellow-500/30 text-yellow-200 text-sm rounded-xl px-4 py-3 mb-4 text-center">
            Sua sessão expirou. Faça login novamente.
          </div>
        )}

        <div className="card p-7">
          <h2 className="font-sora text-xl font-bold text-acre-dark mb-5">Entrar na conta</h2>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="label">E-mail</label>
              <input className="input" type="email" placeholder="seu@email.com"
                {...register('email', { required: 'E-mail obrigatório' })} />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="label mb-0">Senha</label>
                <button type="button" onClick={() => setModalEsq(true)}
                  className="text-xs text-acre-green hover:underline font-medium">
                  Esqueceu a senha?
                </button>
              </div>
              <div className="relative">
                <input className="input pr-10" type={verSenha ? 'text' : 'password'}
                  placeholder="••••••••"
                  {...register('senha', { required: 'Senha obrigatória' })} />
                <button type="button" onClick={() => setVerSenha(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {verSenha ? <EyeOff size={15}/> : <Eye size={15}/>}
                </button>
              </div>
              {errors.senha && <p className="text-red-500 text-xs mt-1">{errors.senha.message}</p>}
            </div>

            <button type="submit" disabled={loading}
              className="btn-primary w-full justify-center py-3 text-base disabled:opacity-60 flex items-center gap-2">
              {loading ? <><Loader2 size={15} className="animate-spin"/> Entrando...</> : 'Entrar'}
            </button>
          </form>

          <div className="mt-5 pt-5 border-t border-gray-100 text-center space-y-2">
            <p className="text-sm text-gray-500">
              Não tem conta?{' '}
              <Link to="/cadastro" className="text-acre-green font-semibold hover:underline">
                Cadastre-se grátis
              </Link>
            </p>
            <button onClick={() => setModalEsq(true)}
              className="text-xs text-gray-400 hover:text-acre-green transition-colors">
              🔐 Redefinir senha por e-mail
            </button>
          </div>

        </div>

        <p className="text-center text-white/40 text-xs mt-6">
          <Link to="/" className="hover:text-white/70">← Voltar ao início</Link>
        </p>
      </div>
    </div>
  );
}
